/**
 * Lifting Service Selector — per-item delivery option (Bordsteinkarte / Aufstellort)
 *
 * Architecture:
 * 1. Capture phase `click` listener intercepts +/- quantity button clicks
 *    and batches parent+service qty changes via /cart/update.js.
 * 2. Capture phase `change` listener intercepts manual quantity typing
 *    (Enter/blur) for the same batch update.
 * 3. <lifting-service-selector> web component provides radio toggle.
 * 4. Cart reconcile safety net: drops orphaned service lines and realigns
 *    service quantities changed outside this module (product page add, remove).
 *
 * ⚠️  Must be loaded as <script type="module"> AFTER global.js.
 *    Module order: global.js (module in <head>) → lifting-service.js (module in <body>).
 */

/* ========================================================================
   0. CART CACHE — promise-based to avoid race conditions
   ======================================================================== */

let _cartCachePromise = null;
let _cartCacheTime = 0;
const CART_CACHE_TTL_MS = 1000;

async function getCachedCart() {
  const now = Date.now();
  if (_cartCachePromise && now - _cartCacheTime < CART_CACHE_TTL_MS) {
    return _cartCachePromise;
  }
  const promise = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
    .then(resp => {
      if (!resp.ok) throw new Error('Failed to fetch cart');
      return resp.json();
    })
    .catch(err => {
      _cartCachePromise = null;
      _cartCacheTime = 0;
      throw err;
    });
  _cartCachePromise = promise;
  _cartCacheTime = now;
  return promise;
}

function invalidateCartCache() {
  _cartCachePromise = null;
  _cartCacheTime = 0;
}

/**
 * Returns the parent line a service line belongs to, or null when it has none.
 *
 * `_service_for` holds the parent's line key from the moment the service was
 * added, but Shopify rehashes every line key (`<variant_id>:<hash>`) when a
 * discount code is applied or removed: the parent stays, its key does not.
 * Same rule as snippets/cart-lifting-match.liquid:
 * 1. the line whose key equals `_service_for`;
 * 2. otherwise the stale services of that variant are paired in cart order
 *    with the variant's lines that no service names exactly.
 */
function findServiceParent(items, serviceItem, liftingProductId) {
  const ref = serviceItem.properties?._service_for;
  if (!ref) return null;

  const pid = String(liftingProductId);
  const parents = items.filter(item => String(item.product_id) !== pid);
  const exact = parents.find(item => item.key === ref);
  if (exact) return exact;

  const services = items.filter(item => String(item.product_id) === pid);
  const liveKeys = new Set(items.map(item => item.key));
  const refs = new Set(services.map(item => item.properties?._service_for).filter(Boolean));
  const variantId = String(ref).split(':')[0];

  const freeParents = parents.filter(item =>
    String(item.variant_id) === variantId && !refs.has(item.key)
  );
  const staleServices = services.filter(item => {
    const r = item.properties?._service_for;
    return r && !liveKeys.has(r) && String(r).split(':')[0] === variantId;
  });
  const rank = staleServices.findIndex(item => item.key === serviceItem.key);
  return freeParents[rank] ?? null;
}

/** The service line linked to the parent line `parentKey`, or undefined */
function findServiceLine(items, parentKey, liftingProductId) {
  const pid = String(liftingProductId);
  return items.find(item =>
    String(item.product_id) === pid &&
    findServiceParent(items, item, pid)?.key === parentKey
  );
}

/* ========================================================================
   1. SHARED BATCH UPDATE — used by both click and change handlers
   ======================================================================== */

/**
 * Batch-update parent + service line quantities via /cart/update.js
 * and dispatch cart:update for Athora DOM morph.
 */
async function batchUpdateCart({ parentLineKey, serviceLineKey, quantity, input }) {
  if (!parentLineKey || !serviceLineKey || isNaN(quantity)) return;

  document.dispatchEvent(new CustomEvent('loadingStart'));

  const cartEl = input.closest('#CartDrawer, #MainCart');
  const lineNum = input.dataset.line;

  // Shimmer and blur effect
  if (cartEl && lineNum) {
    document.querySelectorAll(
      `#CartDrawer-Item-${lineNum} .hdt-shimmer, #CartItem-${lineNum} .hdt-shimmer, [ref="cartTotal"].hdt-shimmer`
    ).forEach(el => {
      if (typeof shimmer === 'function') shimmer(el);
    });
    cartEl.setAttribute('effect-blur', '');
  }

  // Sections for DOM morph
  const container = input.closest('cart-drawer, main-cart');
  const sectionId = container?.getAttribute('section-id');
  const sections = sectionId ? [sectionId, 'cart-json'] : [];

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: { [parentLineKey]: quantity, [serviceLineKey]: quantity },
        sections
      })
    });

    const cartData = await response.json();
    document.dispatchEvent(new CustomEvent('loadingEnd'));
    invalidateCartCache();

    // Reset shimmer
    if (cartEl) {
      document.querySelectorAll('.hdt-shimmer').forEach(el => el.classList.remove('hdt-shimmer'));
      cartEl.removeAttribute('effect-blur');
    }

    if (response.ok) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { source: 'lifting-service-qty', cartData, skipOrphanCleanup: true }
      }));
    } else {
      const { description, message } = cartData;
      document.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { source: 'lifting-service-qty', errors: message || description, message }
      }));
      input.value = input.defaultValue;
    }
  } catch (err) {
    document.dispatchEvent(new CustomEvent('loadingEnd'));
  }
}

/* ========================================================================
   2. CAPTURE PHASE CLICK — intercept +/- button clicks for lifting items
   ======================================================================== */

document.addEventListener('click', async function(event) {
  const button = event.target.closest('.hdt-quantity__button');
  if (!button) return;

  const parentItem = button.closest('[data-has-lifting-service]');
  if (!parentItem) return;

  event.stopPropagation();
  event.preventDefault();

  const input = parentItem.querySelector('.hdt-quantity__input');
  if (!input) return;

  if (button.name === 'plus') input.stepUp();
  else if (button.name === 'minus') input.stepDown();

  await batchUpdateCart({
    parentLineKey: parentItem.dataset.itemLineKey,
    serviceLineKey: parentItem.dataset.serviceLineKey,
    quantity: parseInt(input.value, 10),
    input
  });
}, { capture: true });

/* ========================================================================
   3. CAPTURE PHASE CHANGE — intercept manual quantity typing (Enter/blur)
   ======================================================================== */

document.addEventListener('change', async function(event) {
  const input = event.target;
  if (!input.matches('.hdt-quantity__input')) return;

  const parentItem = input.closest('[data-has-lifting-service]');
  if (!parentItem) return;

  const quantity = parseInt(input.value, 10);
  // Skip if quantity didn't actually change (e.g. after stepUp + blur)
  if (quantity === parseInt(input.defaultValue, 10)) return;

  event.stopPropagation();

  await batchUpdateCart({
    parentLineKey: parentItem.dataset.itemLineKey,
    serviceLineKey: parentItem.dataset.serviceLineKey,
    quantity,
    input
  });
}, { capture: true });

/* ========================================================================
   4. LIFTING SERVICE SELECTOR — web component for radio toggle
   ======================================================================== */

class LiftingServiceSelector extends HTMLElement {
  #controller;
  #isApplyingSelection = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('change', this.#onRadioChange.bind(this), { signal });
    this.#preselectFromCart();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Called by cart_main_update_fn / cart_update_fn after section DOM morph.
   */
  async refresh() {
    await this.#preselectFromCart();
  }

  get lineKey() {
    return this.getAttribute('data-line-key') || '';
  }

  get paidVariantId() {
    return this.getAttribute('data-paid-variant-id') || '';
  }

  get productId() {
    return this.getAttribute('data-product-id') || '';
  }

  async #preselectFromCart() {
    try {
      const cartData = await getCachedCart();
      const hasService = Boolean(findServiceLine(cartData.items || [], this.lineKey, this.productId));
      this.#applySelection(hasService);
    } catch (_) { /* cart fetch failed — keep current selection */ }
  }

  #applySelection(hasPaidService) {
    this.#isApplyingSelection = true;
    try {
      this.querySelectorAll('input[type="radio"]').forEach(radio => {
        const shouldCheck =
          (radio.value === 'aufstellort' && hasPaidService) ||
          (radio.value === 'bordsteinkante' && !hasPaidService);
        radio.checked = shouldCheck;
        radio.setAttribute('aria-checked', String(shouldCheck));
        const option = radio.closest('.hdt-lifting-option');
        if (option) option.classList.toggle('is-selected', shouldCheck);
      });
    } finally {
      this.#isApplyingSelection = false;
    }
  }

  async #onRadioChange(event) {
    if (this.#isApplyingSelection) return;
    const radio = event.target;
    if (radio.type !== 'radio') return;
    if (!radio.closest('lifting-service-selector')) return;

    // Stop propagation — prevent Athora's CartItems.onChange handling the radio
    event.stopPropagation();

    const option = radio.closest('.hdt-lifting-option');
    if (option) option.classList.add('is-loading');

    try {
      if (radio.value === 'aufstellort') {
        await this.#addService();
      } else {
        await this.#removeService();
      }
    } catch (_) { /* error handled within each method */ }
      finally {
      if (option) option.classList.remove('is-loading');
    }
  }

  /** Fetch sections for DOM morph after cart update */
  async #fetchSections() {
    const container = this.closest('cart-drawer, main-cart');
    const sectionId = container?.getAttribute('section-id');
    if (!sectionId) return null;

    try {
      const resp = await fetch(
        `${window.Shopify?.routes?.root || '/'}?sections=${encodeURIComponent(sectionId + ',cart-json')}`
      );
      if (resp.ok) return await resp.json();
    } catch (_) { /* section fetch failed — morph will be skipped */ }
    return null;
  }

  /** Add the lifting service product with _service_for property */
  async #addService() {
    if (!this.paidVariantId || !this.lineKey) return;

    const cartData = await getCachedCart();
    const parent = cartData.items?.find(item => item.key === this.lineKey);
    const parentQty = parent?.quantity || 1;

    // Guard against duplicates
    const existing = findServiceLine(cartData.items || [], this.lineKey, this.productId);
    if (existing) {
      if (existing.quantity !== parentQty) {
        const sections = await this.#fetchSections();
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: { [existing.key || existing.line]: parentQty },
            sections: sections ? Object.keys(sections) : []
          })
        });
        invalidateCartCache();
      }
      return;
    }

    // Add new service line
    const formData = new FormData();
    formData.append('id', this.paidVariantId);
    formData.append('quantity', parentQty);
    formData.append('properties[_service_for]', this.lineKey);

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to add lifting service');
    }

    const newCart = await response.json();
    invalidateCartCache();

    const sections = await this.#fetchSections();
    if (sections) newCart.sections = sections;

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { source: 'lifting-service', cartData: newCart }
    }));
  }

  /** Remove the lifting service line linked to this item */
  async #removeService() {
    if (!this.productId || !this.lineKey) return;

    const cartData = await getCachedCart();
    const serviceItem = findServiceLine(cartData.items || [], this.lineKey, this.productId);
    if (!serviceItem) return;

    const sections = await this.#fetchSections();
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: { [serviceItem.key || String(serviceItem.line)]: 0 },
        sections: sections ? Object.keys(sections) : []
      })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to remove lifting service');
    }

    const newCart = await response.json();
    invalidateCartCache();
    if (sections) newCart.sections = sections;

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { source: 'lifting-service', cartData: newCart }
    }));
  }
}

customElements.define('lifting-service-selector', LiftingServiceSelector);

/* ========================================================================
   5. CART RECONCILE — drop orphaned service lines, realign service quantities
   ======================================================================== */

let _isCleaningOrphans = false;
let _lastCleanupTime = 0;
const CLEANUP_DEBOUNCE_MS = 500;

/**
 * Brings every service line back in line with its parent:
 * - a service line whose parent is gone is zeroed (see findServiceParent:
 *   a key rehashed by a discount code still finds its parent);
 * - a service line whose quantity differs from its parent's is set to it.
 *
 * The second case covers the paths that change a parent's quantity outside
 * batchUpdateCart(): adding the same variant again from the product page
 * merges into the existing line and leaves the service one short.
 * Runs on /cart load and after every cart:update without skipOrphanCleanup.
 * The global name predates the quantity check and is kept for its callers
 * (global.js, sections/main-cart.liquid).
 */
window.hdtCleanOrphanedLiftingLines = async function(liftingProductId) {
  if (_isCleaningOrphans) return 0;

  const now = Date.now();
  if (now - _lastCleanupTime < CLEANUP_DEBOUNCE_MS) return 0;

  _isCleaningOrphans = true;

  try {
    // The cart just changed outside this module, a cached copy may predate it
    invalidateCartCache();
    const cart = await getCachedCart();
    const items = cart.items || [];
    const pid = String(liftingProductId);

    const updates = {};
    for (const item of items) {
      if (String(item.product_id) !== pid || !item.properties?._service_for) continue;

      const parentQty = findServiceParent(items, item, pid)?.quantity ?? 0;
      if (item.quantity !== parentQty) updates[item.key] = parentQty;
    }

    const changed = Object.keys(updates).length;
    if (changed === 0) return 0;

    // Debounce only real writes: an empty pass must not swallow the next mismatch
    _lastCleanupTime = Date.now();

    const sections = ['cart-json'];
    for (const container of document.querySelectorAll('cart-drawer, main-cart')) {
      const sectionId = container.getAttribute('section-id');
      if (sectionId) sections.unshift(sectionId);
    }

    const resp = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections })
    });

    if (resp.ok) {
      const cleanCart = await resp.json();
      invalidateCartCache();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { source: 'lifting-service-cleanup', cartData: cleanCart, skipOrphanCleanup: true }
      }));
    }

    return changed;
  } catch (_) {
    return 0;
  } finally {
    _isCleaningOrphans = false;
  }
};
